import React, { useState, useEffect, useRef } from 'react';
import firebase from '../firebaseConfig';
import '../styles/PersonalNotes.css';

const PersonalNotes = () => {
    const [notes, setNotes] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [selectedColor, setSelectedColor] = useState('#2c3e50');
    const [selectedFontSize, setSelectedFontSize] = useState('medium');
    const [showSaveCheck, setShowSaveCheck] = useState(false);
    const saveTimeoutRef = useRef(null);
    const hideCheckTimeoutRef = useRef(null);
    const lastNotesRef = useRef('');

    // Available colors for writing
    const colorOptions = [
        { name: 'Dark Blue', value: '#2c3e50', icon: 'fas fa-circle' },
        { name: 'Ocean Blue', value: '#4066ff', icon: 'fas fa-circle' },
        { name: 'Forest Green', value: '#27ae60', icon: 'fas fa-circle' },
        { name: 'Sunset Orange', value: '#e67e22', icon: 'fas fa-circle' },
        { name: 'Royal Purple', value: '#8e44ad', icon: 'fas fa-circle' }
    ];

    // Available font sizes
    const fontSizeOptions = [
        { name: 'Small', value: 'small', icon: 'fas fa-font', size: '0.8rem' },
        { name: 'Medium', value: 'medium', icon: 'fas fa-font', size: '1rem' },
        { name: 'Large', value: 'large', icon: 'fas fa-font', size: '1.2rem' }
    ];

    useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
            if (user) {
                setCurrentUser(user);
                await loadNotes(user.uid);
            } else {
                setCurrentUser(null);
                setNotes('');
                lastNotesRef.current = '';
            }
            setIsLoading(false);
        });

        return () => {
            unsubscribe();
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            if (hideCheckTimeoutRef.current) {
                clearTimeout(hideCheckTimeoutRef.current);
            }
        };
    }, []);

    const loadNotes = async (userId) => {
        try {
            console.log('Loading notes for user:', userId);
            const docRef = firebase.firestore().collection('users').doc(userId);
            const docSnapshot = await docRef.get();
            
            if (docSnapshot.exists) {
                const data = docSnapshot.data();
                const personalNotes = data.personalNotes || {};
                const content = personalNotes.content || '';
                console.log('Loaded notes content:', content);
                console.log('Loaded color:', personalNotes.selectedColor);
                console.log('Loaded font size:', personalNotes.selectedFontSize);
                setNotes(content);
                setSelectedColor(personalNotes.selectedColor || '#2c3e50');
                setSelectedFontSize(personalNotes.selectedFontSize || 'medium');
                lastNotesRef.current = content;
                setLastSaved(personalNotes.lastUpdated ? personalNotes.lastUpdated.toDate() : null);
            } else {
                console.log('No existing user document found');
                // Initialize with empty values
                setNotes('');
                setSelectedColor('#2c3e50');
                setSelectedFontSize('medium');
                lastNotesRef.current = '';
                setLastSaved(null);
            }
        } catch (error) {
            console.error('Error loading personal notes:', error);
        }
    };

    const saveNotes = async (notesToSave = notes, colorToSave = selectedColor, fontSizeToSave = selectedFontSize) => {
        if (!currentUser) {
            console.log('No current user, skipping save');
            return;
        }
        
        // Always save, even if content is the same (in case color changed)
        console.log('Saving notes:', notesToSave);
        console.log('Saving color:', colorToSave);
        console.log('Saving font size:', fontSizeToSave);

        setIsSaving(true);
        try {
            const docRef = firebase.firestore().collection('users').doc(currentUser.uid);
            const now = new Date();
            
            const personalNotesData = {
                content: notesToSave,
                selectedColor: colorToSave,
                selectedFontSize: fontSizeToSave,
                lastUpdated: firebase.firestore.Timestamp.fromDate(now)
            };
            
            console.log('Personal notes data being saved:', personalNotesData);
            
            await docRef.set({
                personalNotes: personalNotesData
            }, { merge: true });
            console.log('Notes saved successfully to users collection');

            setLastSaved(now);
            lastNotesRef.current = notesToSave;
            
            // Show check mark and hide it after 5 seconds
            setShowSaveCheck(true);
            if (hideCheckTimeoutRef.current) {
                clearTimeout(hideCheckTimeoutRef.current);
            }
            hideCheckTimeoutRef.current = setTimeout(() => {
                setShowSaveCheck(false);
            }, 5000);
        } catch (error) {
            console.error('Error saving personal notes:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleNotesChange = (e) => {
        const newNotes = e.target.value;
        console.log('Notes changed to:', newNotes);
        setNotes(newNotes);
        
        // Clear existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        
        // Set new timeout for auto-save (5 seconds after last input)
        saveTimeoutRef.current = setTimeout(() => {
            console.log('Auto-saving after 5 seconds...');
            saveNotes(newNotes);
        }, 5000);
    };

    const formatLastSaved = (date) => {
        if (!date) return '';
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
        return date.toLocaleDateString();
    };

    if (isLoading) {
        return (
            <div className="personal-notes-card">
                <div className="notes-header">
                    <h3>
                        <i className="fas fa-sticky-note"></i>
                        Personal Notes
                    </h3>
                </div>
                <div className="notes-loading">
                    <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="personal-notes-card">
            <div className="notes-header">
                <h3>
                    <i className="fas fa-sticky-note"></i>
                    Personal Notes
                </h3>
                <div className="notes-controls">
                    <div className="control-group">
                        <div className="color-picker">
                            {colorOptions.map((color) => (
                                <button
                                    key={color.value}
                                    className={`color-option ${selectedColor === color.value ? 'active' : ''}`}
                                    onClick={() => {
                                        console.log('Color changed to:', color.value);
                                        setSelectedColor(color.value);
                                        // Save immediately when color changes with new color
                                        setTimeout(() => saveNotes(notes, color.value, selectedFontSize), 100);
                                    }}
                                    style={{ '--color': color.value }}
                                    title={color.name}
                                >
                                    <i className={color.icon}></i>
                                </button>
                            ))}
                        </div>
                        <div className="font-size-picker">
                            {fontSizeOptions.map((fontSize) => (
                                <button
                                    key={fontSize.value}
                                    className={`font-size-option ${selectedFontSize === fontSize.value ? 'active' : ''}`}
                                    onClick={() => {
                                        console.log('Font size changed to:', fontSize.value);
                                        setSelectedFontSize(fontSize.value);
                                        // Save immediately when font size changes with new size
                                        setTimeout(() => saveNotes(notes, selectedColor, fontSize.value), 100);
                                    }}
                                    title={fontSize.name}
                                >
                                    <i className={fontSize.icon} style={{ fontSize: fontSize.size }}></i>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="save-indicator">
                        {showSaveCheck && !isSaving && (
                            <i className="fas fa-check-circle" title="Saved"></i>
                        )}
                    </div>
                </div>
            </div>

            <div className="notes-content">
                <textarea
                    className="notes-textarea"
                    placeholder="Write your personal notes here... These are private and only visible to you."
                    value={notes}
                    onChange={handleNotesChange}
                    style={{ 
                        color: selectedColor,
                        fontSize: fontSizeOptions.find(f => f.value === selectedFontSize)?.size || '0.9rem'
                    }}
                    rows={12}
                />
            </div>
        </div>
    );
};

export default PersonalNotes;
